import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import { PERIODS } from "../../../../utils";

import {
  dangerousCodes,
  effluentCodes,
  energyFieldMain,
  kpisCodes,
  kpisFieldMain,
  productionCodes,
  socialCodes,
  wasteCodes,
  waterCodes,
  fuelsCodes,
} from "../../../../utils/codes";
import {
  energyCodes,
} from "../../../../utils/dataAndFormulas/energyForms";

export function reportExcel(data, module, year, t, configFormData = null, siteName = "") {
  const determineKeys = (a) => {
    switch (a) {
      case "social":
        return socialCodes;
      case "energy":
        return energyCodes;
      case "production":
        return productionCodes;
      case "effluent":
        return effluentCodes;
      case "water":
        return waterCodes;
      case "waste":
        return wasteCodes;
      case "kpis":
        return kpisCodes;
      case "dangerous":
        return dangerousCodes;
      case "fuel":
      case "fuels":
        return fuelsCodes;
      default:
        return null;
    }
  };

  // Determinar los campos únicos y si tienen código
  const determineFields = () => {
    const allKeys = Object.keys(determineKeys(module));

    if (configFormData && configFormData.fields && configFormData.fields.length > 0) {
      const activeFieldNames = new Set(
        configFormData.fields
          .filter((f) => f.active !== false)
          .map((f) => f.name)
      );
      return allKeys.filter((key) => activeFieldNames.has(key));
    }

    return allKeys;
  };

  // Obtener todos los campos únicos
  const uniqueFields = determineFields();

  let uniqueMainFields = [];

  if (module === "energyform") {
    uniqueMainFields = energyFieldMain;
  } else if (module === "kpisform") {
    uniqueMainFields = kpisFieldMain;
  }

  const fmt = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? Math.round(n) : "-";
  };

  const calculateTotalAndAverage = (field) => {
    const values = data.map((item) =>
      item[field]
        ? item[field].value && Number.isFinite(item[field].value)
          ? item[field].value
          : 0
        : 0
    );
    const total = values.reduce((acc, val) => acc + val, 0);
    const average = values.length ? total / values.length : 0;
    return { total: Math.round(total), average: Math.round(average) };
  };

  const dataByPeriods = (field, data) => {
    const jsonObject = {};
    PERIODS.map((period) => {
      const item = data.find((d) => d.period === period);
      const raw = item?.[field]?.value;
      jsonObject[t(period)] = raw != null && raw !== "" ? fmt(raw) : "-";
    });
    return jsonObject;
  };

  // const dataByPeriodsSubFields = (fieldMain, field, data) => {
  //   return PERIODS.map((period) => {
  //     const item = data.find((d) => d.period === period);
  //     console.log(item, item && item[fieldMain][field] ? item[fieldMain][field].value + field : "nada");
  //     return {
  //       content:
  //         item && item[fieldMain][field]
  //           ? item[fieldMain][field].value
  //             ? item[fieldMain][field].value
  //             : "-"
  //           : "-",
  //       styles: { valign: "middle", halign: "center" },
  //     };
  //   });
  // };

  const formatDataFields = () => {
    const jsonlist = uniqueFields.map((field) => ({
      [t("code")]: determineKeys(module)[field],
      [t("concept")]: t(field),
      [t("unit")]: "-",
      ...dataByPeriods(field, data),
      [t("total")]: calculateTotalAndAverage(field).total,
      [t("average")]: calculateTotalAndAverage(field).average,
    }));

    return jsonlist;
  };

  // const formatDataSubfield = (fieldMain) => {

  //   return uniqueFields.map((field) => {
  //         return [
  //           {
  //             content: determineKeys(module)[field],
  //             styles: { valign: "middle", halign: "center" },
  //           },
  //           {
  //             content: t(module, field),
  //             styles: { valign: "middle", halign: "center" },
  //           },
  //           { content: "-", styles: { valign: "middle", halign: "center" } },
  //           ...dataByPeriodsSubFields(fieldMain,field, data),
  //           {
  //             content: calculateTotalAndAverage(field).total,
  //             styles: { valign: "middle", halign: "center" },
  //           },
  //           {
  //             content: calculateTotalAndAverage(field).average,
  //             styles: { valign: "middle", halign: "center" },
  //           },
  //         ];
  //       })

  // };

  const dataFinal = () => {
    return formatDataFields();
  };

  const exportToExcel = () => {
    // Convertir el JSON a una hoja de trabajo (worksheet)
    const ws = XLSX.utils.json_to_sheet(dataFinal());

    // Crear un array con las filas: título, subtítulo, encabezados y datos
    const siteLabel = siteName ? ` — ${siteName.toLocaleUpperCase()}` : "";
    const data = [
      [(t("report") + " " + t(module) + siteLabel).toLocaleUpperCase()], // Título
      [], // Subtítulo
      ...XLSX.utils.sheet_to_json(ws, { header: 1 }), // Encabezados y datos
    ];

    // Agregar el subtítulo en la celda D2
    data[1][3] = (t("period_report") + " " + year).toLocaleUpperCase(); // D2 es la cuarta celda en la segunda fila (índice 3)

    // Convertir las filas en una hoja de trabajo
    const wsWithTitles = XLSX.utils.aoa_to_sheet(data);
    // Definir el rango de celdas a combinar
    wsWithTitles["!merges"] = [
      { s: { r: 1, c: 3 }, e: { r: 1, c: 16 } }, // Desde D2 (columna 3) hasta Q2 (columna 16)
    ];

    // Aplicar formato de negrita y primera letra en mayúscula a los encabezados
    const headers = data[3]; // Los encabezados están en la fila 4 (índice 3)

    // Aplicar negrita y mayúsculas a cada encabezado
    headers.forEach((header, index) => {
      // Convertir la primera letra a mayúscula
      const capitalizedHeader =
        header && typeof header === "string"
          ? header.charAt(0).toUpperCase() + header.slice(1)
          : header;

      // Aplicar el estilo de negrita
      wsWithTitles[XLSX.utils.encode_cell({ r: 3, c: index })] = {
        v: capitalizedHeader,
        s: { font: { bold: true } },
      };
    });
    // Crear un libro de trabajo (workbook)
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, wsWithTitles, "Hoja 1");

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, wsWithTitles, "Sheet1");
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    const safeSite = siteName ? `_${siteName.replace(/\s+/g, "_")}` : "";
    saveAs(blob, `reporte_${module}${safeSite}_${year}.xlsx`);
  };

  return exportToExcel();
}
